import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MonoLabel, Em, Card } from "@/site/components/Atoms";

const backendBaseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = backendBaseUrl ? `${backendBaseUrl}/api` : "/api";

const AREAS = [
  "Market entry",
  "Kosovo opportunity assessment",
  "Digital business infrastructure",
  "Trade coordination",
  "Public modernization",
  "Institutional or donor-backed opportunity",
  "Defense-related support or localization",
  "Energy or infrastructure",
  "Mining or natural resources",
  "Other",
];

const CONTACT_METHODS = ["Email", "Phone / WhatsApp", "Video call"];

export default function Explore() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", country: "",
    opportunity_area: AREAS[0], target_market: "",
    description: "", preferred_contact: CONTACT_METHODS[0],
  });
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      setStatus({ state: "error", message: "Please complete the required fields." });
      return;
    }
    if (!privacyConsent) {
      setStatus({
        state: "error",
        message: "Please confirm the Privacy Policy acknowledgement to continue.",
      });
      return;
    }
    setStatus({ state: "submitting", message: "" });
    try {
      await axios.post(`${API}/opportunities`, {
        ...form,
        privacy_consent: privacyConsent,
        marketing_consent: marketingConsent,
      });
      setStatus({
        state: "success",
        message:
          "Thank you for sharing your opportunity. Forealco will review the information provided and respond personally where there is a suitable basis for further discussion.",
      });
      setForm({
        name: "", company: "", email: "", country: "",
        opportunity_area: AREAS[0], target_market: "",
        description: "", preferred_contact: CONTACT_METHODS[0],
      });
      setPrivacyConsent(false);
      setMarketingConsent(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : (detail || "Submission failed. Please try again.");
      setStatus({ state: "error", message: msg });
    }
  };

  return (
    <>
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20">
        <div className="fade-up"><MonoLabel>EXPLORE OPPORTUNITIES</MonoLabel></div>
        <h1 className="h-display fade-up delay-1 mt-6 text-[44px] sm:text-[58px] lg:text-[68px] max-w-4xl">
          Let's shape the right structure <Em>for your opportunity.</Em>
        </h1>
        <p className="fade-up delay-2 mt-7 max-w-2xl text-[15px] leading-[1.85] text-white/70">
          Use this page if you are evaluating a market opportunity, considering Kosovo as a first
          step, exploring a cross-border business route, or looking for a structured way to enter a
          developing market.
        </p>
        <p className="fade-up delay-3 mt-4 max-w-2xl text-[13px] leading-[1.85] text-white/55">
          Forealco works with companies, founders, investors, technology firms, advisory groups,
          institutional solution providers, and selected business development initiatives that require
          more than a simple introduction or standard market report.
        </p>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-16 grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-8">
          <Card className="p-6 md:p-10">
            <form data-testid="opportunity-form" onSubmit={submit} className="grid grid-cols-12 gap-5">
              <Field span={6} label="Name *" testid="field-name">
                <input value={form.name} onChange={update("name")} required />
              </Field>
              <Field span={6} label="Company" testid="field-company">
                <input value={form.company} onChange={update("company")} />
              </Field>
              <Field span={6} label="Email *" testid="field-email">
                <input type="email" value={form.email} onChange={update("email")} required />
              </Field>
              <Field span={6} label="Country" testid="field-country">
                <input value={form.country} onChange={update("country")} />
              </Field>

              <Field span={6} label="Opportunity Area *" testid="field-area">
                <select value={form.opportunity_area} onChange={update("opportunity_area")} required>
                  {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
              <Field span={6} label="Target Market" testid="field-target">
                <input value={form.target_market} onChange={update("target_market")} placeholder="e.g. Kosovo, UK, EU…" />
              </Field>

              <Field span={12} label="Short Description of the Opportunity *" testid="field-description">
                <textarea
                  rows={5}
                  value={form.description}
                  onChange={update("description")}
                  required
                  placeholder="What is the opportunity, what stage is it in, and what kind of structure or support are you exploring?"
                />
              </Field>

              <Field span={6} label="Preferred Contact Method" testid="field-contact">
                <select value={form.preferred_contact} onChange={update("preferred_contact")}>
                  {CONTACT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <div className="col-span-12 md:col-span-6 hidden md:block" />

              {/* Privacy notice */}
              <div
                data-testid="privacy-notice"
                className="col-span-12 mt-2 text-[11.5px] leading-[1.7] text-white/45 border-t border-white/10 pt-5"
              >
                By submitting this form, you acknowledge that the information you provide may be
                processed by Forealco and Erenown LLC for the purpose of reviewing your inquiry and
                responding to your request. Depending on the nature of your inquiry, your information
                may be shared with relevant professional partners, consultants, accountants, legal
                advisors, technology providers, banks, institutions, investors, suppliers, or
                business contacts where necessary to evaluate or respond to your request. Submitting
                this form does not create a client relationship, advisory engagement, partnership,
                agency relationship, fiduciary duty, or contractual obligation unless a separate
                written agreement is concluded.
              </div>

              {/* Required privacy consent */}
              <label
                data-testid="privacy-consent"
                className="col-span-12 flex items-start gap-3 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  data-testid="privacy-consent-input"
                  checked={privacyConsent}
                  onChange={(e) => setPrivacyConsent(e.target.checked)}
                  required
                  className="forealco-checkbox mt-1 shrink-0"
                />
                <span className="text-[12.5px] leading-[1.7] text-white/75">
                  I confirm that I have read and understood the{" "}
                  <Link
                    to="/legal#privacy"
                    className="text-[#9DB4FF] hover:text-white underline-offset-2 hover:underline"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and agree that my information may be processed for the purpose of responding to
                  my inquiry. <span className="text-[#9DB4FF]">*</span>
                </span>
              </label>

              {/* Optional marketing consent */}
              <label
                data-testid="marketing-consent"
                className="col-span-12 flex items-start gap-3 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  data-testid="marketing-consent-input"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="forealco-checkbox mt-1 shrink-0"
                />
                <span className="text-[12.5px] leading-[1.7] text-white/65">
                  I would like to receive occasional updates about Forealco articles, Kosovo
                  business insights, market-entry topics, advisory resources, project opportunities,
                  and partnership opportunities. I understand that I can unsubscribe at any time.
                </span>
              </label>

              <div className="col-span-12 flex items-center justify-end pt-2">
                <button
                  type="submit"
                  data-testid="submit-opportunity"
                  className="btn-primary"
                  disabled={status.state === "submitting" || !privacyConsent}
                  style={{
                    opacity: privacyConsent ? 1 : 0.5,
                    cursor: privacyConsent && status.state !== "submitting" ? "pointer" : "not-allowed",
                  }}
                >
                  {status.state === "submitting" ? "Submitting…" : "Submit Opportunity →"}
                </button>
              </div>

              {status.state === "success" && (
                <div data-testid="form-success" className="col-span-12 mt-2 border border-[#9DB4FF]/40 bg-[#9DB4FF]/5 px-5 py-4 text-[13px] text-[#E8ECF1]">
                  ✓ {status.message}
                </div>
              )}
              {status.state === "error" && (
                <div data-testid="form-error" className="col-span-12 mt-2 border border-red-400/40 bg-red-400/5 px-5 py-4 text-[13px] text-red-300">
                  {status.message}
                </div>
              )}
            </form>
          </Card>
        </div>

        <aside className="col-span-12 lg:col-span-4 space-y-3">
          <Card className="p-6">
            <MonoLabel>WHAT TO EXPECT</MonoLabel>
            <ul className="mt-4 space-y-3 text-[13px] leading-[1.7] text-white/70">
              <li>· A senior Forealco contact will respond personally.</li>
              <li>· Initial discussions are handled with discretion and confidentiality.</li>
              <li>· Initial assessment focuses on structure, feasibility, and fit.</li>
              <li>· Forealco only proceeds where it can credibly add value.</li>
            </ul>
          </Card>
          <Card className="p-6">
            <MonoLabel>WHO WE WORK WITH</MonoLabel>
            <ul className="mt-4 space-y-2 text-[13px] text-white/65">
              <li>Companies & founders</li>
              <li>Investors & strategic partners</li>
              <li>Technology firms</li>
              <li>Advisory & professional groups</li>
              <li>Institutional solution providers</li>
            </ul>
          </Card>
        </aside>
      </section>
    </>
  );
}

function Field({ span = 12, label, testid, children }) {
  return (
    <label data-testid={testid} className={`col-span-12 md:col-span-${span} block`}>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#9DB4FF] mb-2">{label}</div>
      <div className="forealco-field">{children}</div>
      <style>{`
        .forealco-field input,
        .forealco-field select,
        .forealco-field textarea {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.12);
          color: #E8ECF1;
          padding: 12px 14px;
          font-size: 14px;
          font-family: 'Inter', system-ui, sans-serif;
          outline: none;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .forealco-field textarea { resize: vertical; }
        .forealco-field input:focus,
        .forealco-field select:focus,
        .forealco-field textarea:focus {
          border-color: #9DB4FF;
          background: rgba(157,180,255,0.05);
        }
        .forealco-field select option { background: #0E1116; color: #E8ECF1; }
        .forealco-checkbox {
          appearance: none;
          -webkit-appearance: none;
          width: 16px;
          height: 16px;
          border: 1px solid rgba(255,255,255,0.25);
          background: rgba(255,255,255,0.03);
          cursor: pointer;
          position: relative;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .forealco-checkbox:hover { border-color: rgba(157,180,255,0.7); }
        .forealco-checkbox:checked {
          background: #9DB4FF;
          border-color: #9DB4FF;
        }
        .forealco-checkbox:checked::after {
          content: "";
          position: absolute;
          left: 4px;
          top: 1px;
          width: 6px;
          height: 10px;
          border: solid #0E1116;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
      `}</style>
    </label>
  );
}
